import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, DocumentTextIcon, PaperClipIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { documentApi } from '../services/api';
import { DOCUMENT_ACCEPT } from '../utils/fileTypes';

interface RegenerateModalProps {
  open: boolean;
  chapterTitle: string;
  hasExistingContent: boolean;
  onClose: () => void;
  onConfirm: (options: {
    customPrompt: string;
    preserveExisting: boolean;
  }) => void;
}

interface AttachedFile {
  name: string;
  content: string;
  size: number;
}

const RegenerateModal: React.FC<RegenerateModalProps> = ({
  open,
  chapterTitle,
  hasExistingContent,
  onClose,
  onConfirm,
}) => {
  const [customPrompt, setCustomPrompt] = useState('');
  const [preserveExisting, setPreserveExisting] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setCustomPrompt('');
      setPreserveExisting(hasExistingContent);
      setAttachedFiles([]);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open, hasExistingContent]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Build combined prompt with file references
    let combinedPrompt = customPrompt.trim();
    if (attachedFiles.length > 0) {
      const refsText = attachedFiles
        .map((f) => `\n\n【参考资料：${f.name}】\n${f.content.slice(0, 8000)}`)
        .join('');
      combinedPrompt = combinedPrompt + refsText;
    }
    onConfirm({ customPrompt: combinedPrompt, preserveExisting });
  };


  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const textExts = ['txt', 'md', 'csv', 'json', 'xml', 'yaml', 'yml', 'log'];

      if (textExts.includes(ext)) {
        // Read text files locally
        try {
          const text = await file.text();
          setAttachedFiles((prev) => [...prev, { name: file.name, content: text, size: file.size }]);
        } catch {
          setAttachedFiles((prev) => [...prev, { name: file.name, content: `[读取失败: ${file.name}]`, size: file.size }]);
        }
      } else {
        // Upload pdf/docx to backend for text extraction
        setUploadingFile(file.name);
        try {
          const res = await documentApi.uploadFile(file);
          if (res.data?.success && res.data?.file_content) {
            setAttachedFiles((prev) => [...prev, { name: file.name, content: res.data.file_content, size: file.size }]);
          } else {
            setAttachedFiles((prev) => [...prev, { name: file.name, content: `[解析失败: ${res.data?.message || file.name}]`, size: file.size }]);
          }
        } catch {
          setAttachedFiles((prev) => [...prev, { name: file.name, content: `[上传失败: ${file.name}]`, size: file.size }]);
        } finally {
          setUploadingFile(null);
        }
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="card shadow-modal rounded-xl w-[560px] max-h-[85vh] flex flex-col bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="w-5 h-5 text-primary" />
            <h3 className="text-base font-bold text-gray-800">重新生成</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* 章节名称 */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">目标章节</label>
            <p className="text-sm font-medium text-gray-800 bg-slate-50 rounded-lg px-3 py-2">{chapterTitle}</p>
          </div>

          {/* 自定义提示词 */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">
              自定义生成提示词 <span className="text-gray-400">（可选）</span>
            </label>
            <textarea
              ref={textareaRef}
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="输入额外的生成要求，例如：&#10;• 补充近三年的财务数据&#10;• 突出技术方案的创新点&#10;• 使用正式严谨的公文风格"
              rows={4}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder:text-gray-400"
            />
          </div>

          {/* 引用资料 */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block">
              引用资料 <span className="text-gray-400">（可选，支持 .pdf/.docx/.txt/.md/.csv 等文件）</span>
            </label>

            {/* 已附件列表 */}
            {attachedFiles.length > 0 && (
              <div className="mb-2 space-y-1.5">
                {attachedFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-sm">
                    <PaperClipIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="flex-1 truncate text-gray-700">{f.name}</span>
                    <span className="text-xs text-gray-400 shrink-0">{formatSize(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 添加按钮 */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!!uploadingFile}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingFile ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  正在解析 {uploadingFile}...
                </>
              ) : (
                <>
                  <PaperClipIcon className="w-4 h-4" />
                  点击添加引用文件
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={DOCUMENT_ACCEPT}
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {/* 内容保留选项 */}
          {hasExistingContent && (
            <div>
              <label className="text-xs text-gray-500 mb-2 block">生成模式</label>
              <div className="space-y-2">
                <label className="flex items-start gap-2.5 cursor-pointer p-2.5 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                  <input
                    type="radio"
                    name="mode"
                    checked={preserveExisting}
                    onChange={() => setPreserveExisting(true)}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">保留已有内容作为参考</span>
                    <p className="text-xs text-gray-400 mt-0.5">AI 会基于现有内容进行优化和补充</p>
                  </div>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer p-2.5 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                  <input
                    type="radio"
                    name="mode"
                    checked={!preserveExisting}
                    onChange={() => setPreserveExisting(false)}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">全新生成（覆盖现有内容）</span>
                    <p className="text-xs text-gray-400 mt-0.5">忽略当前内容，从头重新生成</p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
          >
            开始生成
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegenerateModal;
